import { Accordion, AccordionDetails, AccordionSummary, Box, FormControlLabel, IconButton, Link, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchAllAuthorizedProviderUserData, fetchAuditLogsForPracticeDOSPreference, fetchPracticeByIdData, fetchPracticeTemplatesData, PracticeTemplateRow, resetPracticeEditInfo, resetPracticeTemplateData, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import { selectIsValueFoundInToken, setSelectedModule, useAuthSlice } from "../../../redux/slice/authSlice";
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, PRACTICE_STATUS, PRODUCT_CATEGORY, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import { Controller, useForm } from "react-hook-form";
import { PracticeInfo } from "../../../redux/slice/practice/practiceRegistrationSlice";
import { SwitchTextTrack } from "./PracticeApprovePopup";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import { AccountBalanceWalletOutlined, ArrowBack, AssignmentOutlined, AssignmentReturnedOutlined, BusinessOutlined, Checklist, DescriptionOutlined, DownloadOutlined, ExpandMore, FileOpenOutlined, GroupsOutlined, HealthAndSafetyOutlined, ListAltOutlined, LocalHospitalOutlined, ManageSearch, ModeOutlined, PaymentsOutlined, PersonAddAltOutlined, PersonOutlined, PictureAsPdfOutlined, PlaceOutlined, TrackChanges } from "@mui/icons-material";
import { useNavigation } from "../../../hooks/useNavigation";
import CustomIconButton from "../../common/CustomIconButton";
import AuditLogDisplayPopup, { AuditLogData } from "./AuditLogDisplayPopup";
import { CreateSearchAgency, fetchAgencyById, fetchAssociateAgencyList, getOverrideAgencyCommissions, resetOverrideAgencyCommissionsList, useAgencyReducer } from "../../../redux/slice/agency/AgencySlice";
import { fetchActiveAgencyExecutives, resetAgencyExecutives, usePracticeApproveSlice } from "../../../redux/slice/practice/practiceApproveSlice";
import MuiCustomTable from "../../common/CustomTable";
import moment from "moment";
import { ProductCommissionAgency } from "./PracticeEditView";
import { Row } from "../agency/AgencyForm";
import { fetchUsedPracticeProviderIDs, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { fetchDocumentById, fetchTemplateById, getUpdatedPracticeRegistrationByPracticeId, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { downloadBase64File, getCategoryDisplayLabel, hideElement } from "../../../utils/Utility";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { fetchPracticeBillerData, updatePracticeBillerInfoObject, usePracticeBillerSlice } from "../../../redux/slice/practice/practiceBillerSlice";
import ActionMenu, { ActionItem } from "../../common/ActionMenu";
import UploadBaaPopup from "../baa/UploadBaaPopup";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";
import { useListPageStyles } from "../../common/useListPageStyles";
import SectionTabNav, { SectionTabItem } from "../../common/SectionTabNav";

interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: "right" | "center" | "left";
    format?: (value: number) => string;
}

const PracticeDetailsView = () => {
    const theme = useTheme();
    const {
        tokens: mTokens,
        isDark: isDarkModal,
        cancelBtnSx,
    } = useModalStyles();
    const { tableSx } = useListPageStyles();
    const cardSx = {
        borderRadius: "16px",
        border: `1px solid ${mTokens.border}`,
        backgroundColor: mTokens.bg,
        backgroundImage: "none",
        overflow: "hidden" as const,
        boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
    };
    const sectionBodySx = { px: { xs: 1.25, md: 2 }, py: 1.25 };
    // Same table chrome as PracticeEditView: shared list-page typography and row
    // treatment, but the Action column keeps CustomIconButton's semantic colours.
    const modernTableSx = {
        ...tableSx,
        mt: 0,
        "& .MuiTableHead-root .MuiTableCell-root": {
            ...(tableSx["& .MuiTableHead-root .MuiTableCell-root"] as object),
            background: "transparent !important",
            // Plain-text head cells (the Authorized Users table) have no Typography to colour.
            color: "#00B8D9",
            fontSize: "0.78rem",
            fontWeight: 800,
            letterSpacing: "0.02em",
        },
        "& .MuiTableBody-root .MuiTableCell-root[colspan]": {
            py: 3.5,
            borderBottom: "none",
        },
        "& .MuiTableBody-root .MuiTableCell-root[colspan] .MuiTypography-root": {
            color: mTokens.textSecondary,
            fontSize: "0.85rem",
            fontWeight: 600,
        },
        "& .MuiIconButton-root": {
            borderRadius: "8px",
            "&:hover": {
                backgroundColor: isDarkModal ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
            },
        },
    };
    const outlineAccentBtnSx = {
        textTransform: "none" as const,
        fontWeight: 700,
        fontSize: "0.82rem",
        borderRadius: "10px",
        height: 36,
        px: 1.6,
        color: mTokens.accent,
        borderColor: mTokens.accent,
        backgroundColor: "transparent",
        whiteSpace: "nowrap" as const,
        letterSpacing: "0.04em",
        "&:hover": {
            backgroundColor: `${mTokens.accent}14`,
            borderColor: mTokens.accent,
            boxShadow: "none",
        },
        "& .MuiTypography-root": { color: `${mTokens.accent} !important` },
        "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: mTokens.accent },
    };
    const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
        <Box display="flex" alignItems="flex-start" py={0.4} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" }, gap: 1 }}>
            <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "38%", maxWidth: "42%", minWidth: 88, flexShrink: 0, pt: 0.15 }}>
                {label}
            </Typography>
            <Box sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                {value ?? "—"}
            </Box>
        </Box>
    );
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { practiceEditInfo, providerAuthorizedUserData,practiceTemplateData } = usePracticeEditSlice();
    const { overrideAgencyCommissionsList, associateAgencyList } = useAgencyReducer();
    const { usedPracticeProviderIds } = usePracticeProvidersSlice();
    const { practiceBillerInfo } = usePracticeBillerSlice();
    const { agencyExecutives } = usePracticeApproveSlice();
    const { userInfo } = useAuthSlice();
    const { control } = useForm<PracticeInfo>();
    const { navigate } = useNavigation();
    const { allCodeTablesValuesMap } = useCodetablesSlice();
    const [auditLogPopupShow, setAuditLogPopupShow] = React.useState<{
        auditLogData: AuditLogData[];
        show: boolean;
    }>({ auditLogData: [], show: false });
    const isDownloading = useRef(false);
    const canAccessUsersList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.USER_MANAGEMENT)(state));
    const canAccessProvidersList = useSelector((state: RootState) => selectIsValueFoundInToken("canManageUsers", USER_ROLES.ROLE_PROVIDER)(state));
    const [showPDF, setShowPDF] = useState(false);
    const { document } = usePracticeSlice(); 
    const canAccessPracticeActionsEdit = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPracticeActions", PRACTICE_STATUS.EDIT)(state));
    const canAccessPracticeActionsPreview = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPracticeActions", PRACTICE_STATUS.PREVIEW)(state));
    const canAccessPracticeActionsApprove = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPracticeActions", PRACTICE_STATUS.APPROVE)(state));
    const canAccessPracticeActionsAchieve = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPracticeActions", PRACTICE_STATUS.ACHIEVE)(state));
    const canAccessPracticeActionsImportBaa = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPracticeActions", PRACTICE_STATUS.IMPORT_BAA)(state));
    const canAuditPractice = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.PRACTICEAUDIT)(state));
    const [showBaaPopup, setShowBaaPopup] = useState<{show: boolean, userEntity: "PRACTICE" | "USER", details:PracticeInfo | null}>();
    const [activeTableTab, setActiveTableTab] = useState<string>("");
    
    const overrideEntityColumns: Column[] = [
        { id: "productName", label: "Product", minWidth: 100 },
        { id: "effectiveDate", label: "Effective Date", minWidth: 100 },
        { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
        { id: "overrideAgency", label: "Override Agency", minWidth: 100 },
        { id: "commissionRate", label: "Service Fee", minWidth: 100 },
        { id: "associateEntityName", label: "Associate Entity", minWidth: 100 },
        { id: "associateAgencyCommission", label: "Associate Entity Svc Fee", minWidth: 100, format: (value: number) => { return value + " %" } },
    ];

    const authorizedSignerColumns: Column[] = [
        { id: "fullTitleName", label: "Full Name", minWidth: 100 },
        { id: "email", label: "Email", minWidth: 100 },
        { id: "isOwner", label: "Ownership", minWidth: 100 },
        { id: "position", label: "Position", minWidth: 100 },
        { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
    ];

    const locationColumns: Column[] = [
        {
            id: "combinedAddress",
            label: "Practice Location Address",
            minWidth: 100,
        },
        { id: "billingAddress", label: "Billing Address", minWidth: 100 },
        { id: "modifiedInactiveDate", label: "Inactive Date", minWidth: 100 },
    ];

    const providerColumn: Column[] = [
        { id: "fullName", label: "Provider Name", minWidth: 120 },
        { id: "npi", label: "NPI", minWidth: 100 },
        { id: "ptan", label: "PTAN", minWidth: 100 },
        { id: "taxId", label: "TAX ID", minWidth: 100 },
        { id: "email", label: "Email", minWidth: 200 },
        { id: "userCreated", label: "User Created", minWidth: 100 },
        { id: "modifiedInactiveDate", label: "Inactive Date", minWidth: 100 },
    ];
    const practiceTemplateColumn: Column[] = useMemo(() => {
        let columnArray: Column[] = [
            { id: "type", label: "Type" },
            { id: "docName", label: "Name", minWidth: 120 },
            { id: "description", label: "Description", minWidth: 120 },
            { id: "manufacturerName", label: "Manufacturer", minWidth: 120 },//CS-1518
            { id: "templateLevel", label: "Template Level", minWidth: 120 },//CS-1518
            { id: "inUse", label: "In Use", minWidth: 120 },
            { id: "inactiveDate", label: "Inactive Date" },
            { id: "action", label: "Actions" },
        ];
        return columnArray;
    }, []);

    //Unmount
    useEffect(() => () => {
        dispatch(resetPracticeEditInfo());
        dispatch(updatePracticeBillerInfoObject(undefined));
        dispatch(resetAgencyExecutives());
        dispatch(resetPracticeTemplateData());
        dispatch(resetOverrideAgencyCommissionsList());
    }, [])

    const handleActionPreview = async () => {
        const data = {
            practiceId: practiceEditInfo?.practiceId,
            updatePracticeAgreement: false,
        };
        const response = await dispatch(getUpdatedPracticeRegistrationByPracticeId(data));
        const documentData = response.payload;

        if (documentData && documentData.data) {
            setShowPDF(true);
        }
        else {
            let errorObj: SingleAlertInfo = {
                message: "Feature currently under development. Stay tuned for updates!",
                alertType: "info"
            }
            dispatch(setSingleAlertObj(errorObj));
        }
    }

    // const handleActionEdit = () => {
    //     const approvalStatus = practiceEditInfo?.approvalStatus; // CS-1555
    //     navigate("/practice_edit_view", { state: { practiceEditInfo: { approvalStatus }, practiceId: practiceEditInfo?.practiceId, documentId: practiceEditInfo?.associateAgreementDocumentId } }); // CS-1555
    // }

    const handleApproveAction = async () => {
        const response = await dispatch(fetchPracticeByIdData(practiceEditInfo?.practiceId ?? ""));
        if (response.payload) {
            navigate('/practice_request', { state: { rowValue: response.payload?.data } })
        }
    }

    const handlePracticeAchievePreview = async () => {
        if (practiceEditInfo?.associateAgreementDocumentId !== null) {
            const actionResult = await dispatch(fetchDocumentById(practiceEditInfo!.associateAgreementDocumentId!.toString()));
            const documentData = actionResult.payload;
            if (documentData && documentData.content) {
                setShowPDF(true);
            }
        } else {
            let errorObj: SingleAlertInfo = {
                message: "Feature currently under development. Stay tuned for updates!",
                alertType: "info"
            }
            dispatch(setSingleAlertObj(errorObj));
        }
    }

    const handleImportBaa = () => {
        setShowBaaPopup({
            show: true,
            userEntity: "PRACTICE",
            details: location?.state?.practiceData
        });
    }
    const handleClickPracticeAudit = () => {
        if (practiceEditInfo && practiceEditInfo?.practiceName) {
            const PracticeAuditData = {
                entityName: "PRACTICE",
                entityNumber: practiceEditInfo?.practiceName
            }

            const url = `/auditlog?entityName=${PracticeAuditData.entityName}&entityNumber=${PracticeAuditData.entityNumber}`;
            window.open(url, '_blank');
            // navigate("/auditlog", '_blank',{ state: { entityName: IVRAuditData.entityName, entityNumber: IVRAuditData.entityNumber } });
        }
    }

    const actions: ActionItem[] = [
        {
            label: "PDF",
            icon: <PictureAsPdfOutlined fontSize="small" />,
            color: "info",
            onClick: () => handleActionPreview(),
            isHidden: !((practiceEditInfo?.approvalStatus === "APPROVED" || practiceEditInfo?.approvalStatus === "REJECTED") &&
                practiceEditInfo?.approvalStatus === "APPROVED" && canAccessPracticeActionsPreview)
        },
        // {
        //     label: "Edit",
        //     icon: <ModeOutlined fontSize="small" />,
        //     color: "info",
        //     onClick: () => handleActionEdit(),
        //     isHidden: !(practiceEditInfo?.approvalStatus === "APPROVED" || practiceEditInfo?.approvalStatus === "REJECTED") ||
        //         !canAccessPracticeActionsEdit
        // },
        {
            label: "Approve",
            icon: <Checklist fontSize="small" />,
            color: "success",
            onClick: () => handleApproveAction(),
            isHidden: (practiceEditInfo?.approvalStatus === "APPROVED" || practiceEditInfo?.approvalStatus === "REJECTED") ||
                !canAccessPracticeActionsApprove
        },
        {
            label: "Show Archive Agreement",
            icon: <FileOpenOutlined fontSize="small" />,
            color: "info",
            onClick: () => handlePracticeAchievePreview(),
            isHidden: practiceEditInfo?.approvalStatus !== "APPROVED" || !canAccessPracticeActionsAchieve
        },
        {
            label: "Import BAA",
            icon: <AssignmentReturnedOutlined fontSize="small" />,
            color: "info",
            onClick: () => handleImportBaa(),
            isHidden: practiceEditInfo?.approvalStatus !== "APPROVED" ||
                !canAccessPracticeActionsImportBaa ||
                location?.state?.practiceData?.baaDetailId !== null
        },
        {
            label: "Practice Audit",
            icon: <TrackChanges fontSize="small" />,
            color: "warning",
            onClick: () => handleClickPracticeAudit(),
            isHidden: !canAuditPractice
        },
    ];

    const hasVisibleActions = useMemo(() => {
        return actions.some(action => !action.isHidden);
    }, [actions]);

    useEffect(() => {
        dispatch(setSelectedModule("Practice"));
        if (location?.state?.practiceData?.practiceId) {
            dispatch(fetchPracticeByIdData(location?.state?.practiceData?.practiceId));
            dispatch(fetchAssociateAgencyList());
            dispatch(fetchActiveAgencyExecutives({ getAgencyAdmins: true, isInactive: true }));
            dispatch(getOverrideAgencyCommissions(location?.state?.practiceData?.practiceId));
            dispatch(fetchAllAuthorizedProviderUserData(location?.state?.practiceData?.practiceId))
            dispatch(fetchUsedPracticeProviderIDs());
            dispatch(fetchPracticeTemplatesData(location?.state?.practiceData?.practiceId))
            dispatch(fetchPracticeBillerData(location?.state?.practiceData?.practiceId));
            
        }
    }, [])

    const accountRepresentativeData = agencyExecutives?.filter((item) => item.agencyId === practiceEditInfo?.agencyId) || [];
    const findProductCommission = (commission: ProductCommissionAgency) => {
        if (associateAgencyList && associateAgencyList.length > 0) {
            const associateAgency: CreateSearchAgency | undefined = associateAgencyList?.find((associateAgency: CreateSearchAgency) => commission.associateAgencyId == associateAgency.agencyId);
            if (associateAgency && associateAgency.agencyProductCommisions) {
                const associateProdComm: ProductCommissionAgency | undefined = associateAgency.agencyProductCommisions?.find((prodComm: ProductCommissionAgency) => prodComm.productId === commission.productId);
                if (associateProdComm && associateProdComm.commissionRate) {
                    return associateProdComm.commissionRate;
                }
            }
        }
        return "0%";
    }
    const commissionRow: Row[] = useMemo(() => {
        let commission: Row[] = [];
        if (overrideAgencyCommissionsList) {
            let rowIndex = 0;

            overrideAgencyCommissionsList.forEach((commissionItem: ProductCommissionAgency) => {
                commission.push({
                    productCommissionId: commissionItem.productCommissionId,
                    productId: commissionItem.productId,
                    productName: commissionItem.productName,
                    associateType: commissionItem.associateType,
                    associateAgencyId: commissionItem.associateAgencyId,
                    associatePracticeId: commissionItem.associatePracticeId,
                    associateAccountRepId: commissionItem.associateAccountRepId,
                    commissionRate: commissionItem.commissionRate + " %",
                    commissionPayTerms: commissionItem.commissionPayTerms,
                    associateAgencyName: commissionItem.associateAgencyName,
                    associateAccountRepName: commissionItem.associateAccountRepName,
                    associatePracticeName: commissionItem.associatePracticeName,
                    associateEntityName: commissionItem.associateAgencyName || commissionItem.associateAccountRepName || commissionItem.associatePracticeName,
                    associateAgencyCommission: commissionItem.associateAgencyId ? findProductCommission(commissionItem) : "",
                    overrideAgency: commissionItem.agencyName,
                    effectiveDate: commissionItem.effectiveDate ? moment(commissionItem.effectiveDate?.toString())?.format("MM/DD/YYYY") : "",
                    inactiveDate: commissionItem.inactiveDate ? moment(commissionItem.inactiveDate?.toString())?.format("MM/DD/YYYY") : "",
                    rowIndex: rowIndex++,
                });
            });
        }
        return commission;
    }, [overrideAgencyCommissionsList]);

    const authorizedSignersInfo = useMemo(() => {
        if (practiceEditInfo) {
            return practiceEditInfo.practiceRequestInfo?.authorizedSigners?.map((info) => ({
                ...info,
                fullTitleName: info.title !== null ? info.title + " " + info.fullName : info.fullName,//CS-1425 
                email: info.email
            }));
        }
        return [];
    }, [practiceEditInfo]);

    const modifiedPracticeLocationInfo = useMemo(() => {
        if (practiceEditInfo) {
            return practiceEditInfo?.practiceLocations?.map((info) => ({
                ...info,
                combinedAddress: `${info.streetAddress}${info.unitNumber ? ', ' + info.unitNumber : ''}${info.city ? ', ' + info.city : ''}${info.state ? ', ' + info.state : ''}${info.zipCode ? ', ' + info.zipCode : ''}`,
                modifiedInactiveDate:
                    info.inactiveDate !== null
                        ? moment(info.inactiveDate).format("MM/DD/YYYY")
                        : "",
                billingAddress: info.locationType === "BILLING" ? "Yes" : "No"
            }));
        }
        return [];
    }, [practiceEditInfo]);

    const modifiedPracticeProvidersInfo = useMemo(() => {
        if (!practiceEditInfo) return [];

        const mappedData = (practiceEditInfo?.practiceProviders ?? []).map((info) => {
            const isExistingUserProfile = usedPracticeProviderIds?.includes(info.providerId) ?? false;

            return {
                ...info,
                fullName: isExistingUserProfile ? (
                    <Link
                        component="button"
                        underline="hover"
                        variant="body2"
                        onClick={() => handleUserListNavigate(info)}
                        sx={{ color: mTokens.accent }}
                    >
                        {`${info.title ? info.title + " " : " "} ${info.firstName} ${info.middleName ? info.middleName + " " : ""
                            }${info.lastName}`}
                    </Link>
                ) : (
                    `${info.title ? info.title + " " : " "} ${info.firstName} ${info.middleName ? info.middleName + " " : ""
                    }${info.lastName}`),
                modifiedInactiveDate: info.inactiveDate
                    ? moment(info.inactiveDate).format("MM/DD/YYYY")
                    : "",
                userCreated: isExistingUserProfile ? "Yes" : "No",
                isExistingUserProfile,
            };
        });
        return mappedData.sort((a, b) => {
            if (a.inactiveDate && !b.inactiveDate) return 1;
            if (!a.inactiveDate && b.inactiveDate) return -1;
            return 0;
        });
    }, [practiceEditInfo, usedPracticeProviderIds]);

      
    const practiceTemplateRows: PracticeTemplateRow[] = useMemo(() => {
        const rows: PracticeTemplateRow[] = [];
        practiceTemplateData?.forEach(data => {
            const manufacturerName = data?.manufacturerName || 'N/A';
            const hasAsterisk = manufacturerName.includes('*');

            const row = {
              type: data.type,
              docName: data.fileName,
              description: data.description,
              inactiveDate: data.inactiveDate,
              templateId: data.templateId,
              manufacturerPracticeTemplateId: data.manufacturerPracticeTemplateId,//CS-1518
              manufacturerId: data?.manufacturerId,
              manufacturerName: hasAsterisk ? (
                <span>
                  {manufacturerName.replace('*', '')}
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          padding: 2,
                          maxWidth: 400,
                          backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                          borderRadius: 2,
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0px 4px 8px rgba(0, 0, 0, 0.5)'
                            : '0px 4px 8px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#322f2c',
                            lineHeight: 1.5,
                            letterSpacing: '0.02em',
                          }}
                        >
                          This template is used by more than one manufacturers.
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                          color: theme.palette.mode === 'dark' ? '#fff' : '#333',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0px 3px 6px rgba(0, 0, 0, 0.4)'
                            : '0px 3px 6px rgba(0, 0, 0, 0.08)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          padding: 0,
                        },
                      },
                    }}
                  >
                    <span style={{  color: '#ff9800', fontWeight: 'bold' }}>*</span>
                  </Tooltip>
                </span>
              ) : manufacturerName,//CS-1518
              templateLevel: data?.templateLevel || 'N/A',//CS-1518
              inUse: data.inactiveDate ? 'No' : 'Yes',
            }
            rows.push(row)
        })
        return rows;
      }, [practiceTemplateData]);  

    const usedIdsSet = new Set(usedPracticeProviderIds);
    const groups = (providerAuthorizedUserData || []).map((p: any) => ({
        providerId: p.providerId,
        providerName: p.providerName || 'All',
        providerRaw: p, // keep the raw provider if needed for navigation
        hasProfile: usedIdsSet.has(p.providerId),
        users: (p.users || []).map((u: any) => ({
            email: u.email,
            userRole: u.userRole,
            addedBy: u.addedBy,
            addedOn: u.addedOn,
            isNotificationEnabled: u.isNotificationEnabled,
            providerAuthorizedUserId: u.providerAuthorizedUserId,
            userId: u.userId
        })),
    }));

    const isAdminUser = userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN;
    const hasTemplatesTab = isAdminUser && practiceTemplateRows?.length > 0;
    const hasAuthorizedUsersTab = groups?.length > 0;
    // Tables are grouped into tabs (same pattern as the practice edit view) so the
    // page shows one related set at a time instead of one long stack.
    const tableTabs = useMemo<SectionTabItem[]>(() => {
        const tabs: SectionTabItem[] = [];
        if (hasTemplatesTab) {
            tabs.push({ id: "templates", label: "Practice Templates", icon: <DescriptionOutlined fontSize="small" /> });
        }
        if (isAdminUser) {
            tabs.push({ id: "override", label: "Override Entity", icon: <TrackChanges fontSize="small" /> });
        }
        tabs.push({ id: "signers", label: "Authorized Signers & Locations", icon: <GroupsOutlined fontSize="small" /> });
        if (hasAuthorizedUsersTab) {
            tabs.push({ id: "authorizedUsers", label: "Authorized Users", icon: <PersonAddAltOutlined fontSize="small" /> });
        }
        tabs.push({ id: "providers", label: "Providers", icon: <LocalHospitalOutlined fontSize="small" /> });
        return tabs;
    }, [hasTemplatesTab, isAdminUser, hasAuthorizedUsersTab]);
    const selectedTableTab = tableTabs.some((tab) => tab.id === activeTableTab)
        ? activeTableTab
        : (tableTabs[0]?.id || "");

    const handleUserListNavigate = (row: any) => {
        navigate("/user_list", { state: { providerData: { firstName: row.firstName, lastName: row.lastName, entityIds: practiceEditInfo?.practiceId } } });
    };

    const onAuditHistoryClick = async () => {
        if (practiceEditInfo?.practiceId) {
            const response = await dispatch(fetchAuditLogsForPracticeDOSPreference(parseInt(practiceEditInfo.practiceId)));
            if (response.payload && response.payload.message === "SUCCESS") {
                setAuditLogPopupShow({ auditLogData: response.payload.data, show: true })
            }
        }
    };

     const handlePracticeTemplateDownload = async (templateId: string) => {
        if (isDownloading.current) return;
        isDownloading.current = true;
    
        try {
          const actionResult = await dispatch(fetchTemplateById(templateId));
          const documentData = actionResult.payload;
          if (documentData && documentData.content) {
          downloadBase64File(documentData.content, documentData.templateFileName, 'image/jpeg');
          }
    
        } finally {
          isDownloading.current = false;
        }
      };
     const practiceTemplateActionComponent = (row: any): JSX.Element => {
        
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <CustomIconButton
              color="success"
              size="small"
              variant="outlined"
              tooltipMessage="Download"
              tooltipPlacement="top"
              onClick={() => handlePracticeTemplateDownload(row.templateId)}
            >
              <DownloadOutlined fontSize="small" />
            </CustomIconButton>
          </Box>
        );
      };

       const downloadPracticeRegistration = async () => {
          const documentId = practiceEditInfo?.associateAgreementDocumentId
          if (documentId && documentId > 0) {
            const response = await dispatch(fetchDocumentById(String(documentId)));
            const documentData = response.payload;
            if (documentData && documentData.content) {
              setShowPDF(true);
            }
            else {
              let errorObj: SingleAlertInfo = {
                message: `Associate agreement document not exists for this practice Id ${practiceEditInfo?.practiceId} please contact administrator`,
                alertType: "info"
              }
              dispatch(setSingleAlertObj(errorObj));
            }
      
          } else {
            let errorObj: SingleAlertInfo = {
              message: "Not found associate agreement document",
              alertType: "error",
            };
            dispatch(setSingleAlertObj(errorObj));
          }
        };

    const handleClose = () => {
        setShowPDF(false);
    }

    return (
        <>
            <form>
                <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
                    <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                            <PageBackButton tooltip={null} />
                            <Box data-testid="header">
                                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
                                    Practices / Details
                                </Typography>
                                <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
                                    Practice Details
                                </Typography>
                                <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
                                    Review practice information, contacts, locations, and settings.
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Button
                                color="info"
                                variant="outlined"
                                removeUpperCase
                                startIcon={<FileOpenOutlined />}
                                onClick={() => {
                                    downloadPracticeRegistration();
                                }}
                                sx={outlineAccentBtnSx}
                            >
                                PREVIEW ARCHIVE AGREEMENT
                            </Button>
                            {(practiceEditInfo?.approvalStatus === "APPROVED" || practiceEditInfo?.approvalStatus === "REJECTED") && (
                                <>
                                    <Button
                                        color="info"
                                        variant="outlined"
                                        removeUpperCase
                                        onClick={() =>
                                            navigate("/practice_edit_view", { state: { practiceId: practiceEditInfo?.practiceId, documentId: practiceEditInfo?.associateAgreementDocumentId } })
                                        }
                                        style={{ display: `${hideElement(!canAccessPracticeActionsEdit)}` }}
                                        startIcon={<ModeOutlined fontSize="small" />}
                                        sx={outlineAccentBtnSx}
                                    >
                                        Edit
                                    </Button>
                                </>
                            )}
                            <ActionMenu label="Action" actions={actions} disabled={!hasVisibleActions} removeUpperCase sx={outlineAccentBtnSx} />
                        </Box>
                    </Box>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.9fr) minmax(0, 0.85fr)" }, gap: 2, alignItems: "flex-start" }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                order: { xs: 1, md: 2 },
                                minWidth: 0,
                                position: { md: "sticky" },
                                top: { md: 12 },
                                alignSelf: "start",
                            }}
                        >
                            <Box sx={cardSx}>
                                <SectionHeader icon={<BusinessOutlined fontSize="small" />} title="Practice Info" />
                                <Box sx={{ px: 2.8, py: 1 }}>
                                    <InfoRow label="Practice Name" value={practiceEditInfo?.practiceName || "-"} />
                                    <InfoRow label="Practice Tax ID" value={practiceEditInfo?.taxId || "-"} />
                                    <InfoRow label="Practice NPI" value={practiceEditInfo?.npi || "-"} />
                                    <InfoRow label="Practice PTAN" value={practiceEditInfo?.ptan || "-"} />
                                    <InfoRow
                                        label="Contact"
                                        value={practiceEditInfo?.contactFirstName ? (`${practiceEditInfo?.contactFirstName + " "} ${practiceEditInfo?.contactMiddleName ? practiceEditInfo?.contactMiddleName + " " : ""} ${practiceEditInfo?.contactLastName}`) : "-"}
                                    />
                                    <InfoRow label="Contact Email" value={practiceEditInfo?.contactEmail || "-"} />
                                    <InfoRow label="Contact Phone" value={practiceEditInfo?.contactPhone || "-"} />
                                    <InfoRow label="Contact Fax" value={practiceEditInfo?.contactFax || "-"} />
                                </Box>
                            </Box>

                            <Box sx={cardSx}>
                                <SectionHeader icon={<PaymentsOutlined fontSize="small" />} title="Biller Info" />
                                <Box sx={{ px: 2.8, py: 1 }}>
                                    <InfoRow label="Biller Name" value={practiceBillerInfo?.biller?.billerName || "-"} />
                                    <InfoRow label="Contact Email" value={practiceBillerInfo?.biller?.email || "-"} />
                                    <InfoRow label="Contact Phone" value={practiceBillerInfo?.biller?.phone || "-"} />
                                </Box>
                            </Box>

                            <Box sx={cardSx}>
                                <SectionHeader
                                    icon={<AccountBalanceWalletOutlined fontSize="small" />}
                                    title="Account Payable Info"
                                    subtitle="Account Payable Information."
                                />
                                <Box sx={{ px: 2.8, py: 1 }}>
                                    <InfoRow
                                        label="AP"
                                        value={practiceEditInfo?.apFirstName ? (`${practiceEditInfo?.apFirstName + " "} ${practiceEditInfo?.apMiddleName ? practiceEditInfo?.apMiddleName + " " : ""} ${practiceEditInfo?.apLastName}`) : "-"}
                                    />
                                    <InfoRow label="Contact Email" value={practiceEditInfo?.apEmail || "-"} />
                                    <InfoRow label="Contact Phone" value={practiceEditInfo?.apPhone || "-"} />
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 2, md: 1 }, minWidth: 0 }}>
                            <Box sx={cardSx}>
                                <SectionHeader icon={<AssignmentOutlined fontSize="small" />} title="Other Info" />
                                <Box sx={{ px: 2.8, py: 1 }}>
                                    <InfoRow label="Billing Type" value={practiceEditInfo?.billingType || "-"} />
                                    {practiceEditInfo?.practiceRequestInfo?.dmeLicense != null && (
                                        <InfoRow
                                            label="Do you have a DME license?"
                                            value={practiceEditInfo?.practiceRequestInfo?.dmeLicense ? "Yes" : "No"}
                                        />
                                    )}
                                    {practiceEditInfo?.practiceRequestInfo?.billingCollagen != null && (
                                        <InfoRow
                                            label={`Will you be billing ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}?`}
                                            value={practiceEditInfo?.practiceRequestInfo?.billingCollagen ? "Yes" : "No"}
                                        />
                                    )}
                                    {practiceEditInfo?.practiceRequestInfo?.billMedicare != null && (
                                        <InfoRow
                                            label="Will you bill Medicare?"
                                            value={practiceEditInfo?.practiceRequestInfo?.billMedicare ? "Yes" : "No"}
                                        />
                                    )}
                                    {practiceEditInfo?.practiceRequestInfo?.billMedicareAdvantage != null && (
                                        <InfoRow
                                            label="Will you bill Medicare Advantage?"
                                            value={practiceEditInfo?.practiceRequestInfo?.billMedicareAdvantage ? "Yes" : "No"}
                                        />
                                    )}
                                    {practiceEditInfo?.practiceRequestInfo?.billCommercial != null && (
                                        <InfoRow
                                            label="Will you bill Commercial?"
                                            value={practiceEditInfo?.practiceRequestInfo?.billCommercial ? "Yes" : "No"}
                                        />
                                    )}
                                    <InfoRow
                                        label="Product Category"
                                        value={
                                            practiceEditInfo?.practiceRequestInfo?.interestedProductCategory
                                                ?.map((cat: string) => getCategoryDisplayLabel(allCodeTablesValuesMap, cat))
                                                .join(", ") || "-"
                                        }
                                    />
                                    {/* CS-1728 - Replacing static label to dynamic constant */}
                                    {practiceEditInfo?.practiceRequestInfo?.interestedProductCategory?.some(
                                        (cat: string) => cat === PRODUCT_CATEGORY.CONSERVATIVE
                                    ) && (
                                        <InfoRow
                                            label="Date of Service Preference"
                                            value={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <span>{practiceEditInfo?.dosPreference || "-"}</span>
                                                    {(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) && (
                                                        <CustomIconButton
                                                            color="info"
                                                            size="small"
                                                            variant="outlined"
                                                            tooltipMessage="Audit Trail"
                                                            onClick={onAuditHistoryClick}
                                                            tooltipPlacement="bottom"
                                                        >
                                                            <ManageSearch />
                                                        </CustomIconButton>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    )}
                                </Box>
                            </Box>

                            {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                                <Box sx={cardSx}>
                                    <SectionHeader icon={<HealthAndSafetyOutlined fontSize="small" />} title="Specialization" />
                                    <Box sx={{ px: 2.8, py: 1 }}>
                                        <InfoRow label="Type of Specialization(s)" value={practiceEditInfo?.specialization || "-"} />
                                        {practiceEditInfo?.specialization !== null && (
                                            <>
                                                <InfoRow label="Wound Care Focus" value={practiceEditInfo?.practiceRequestInfo?.woundCareFocus || "-"} />
                                                {practiceEditInfo?.practiceRequestInfo?.woundCareFocus !== "Not Available" && (
                                                    <InfoRow
                                                        label="Average Wounds Treated Per Month"
                                                        value={practiceEditInfo?.practiceRequestInfo?.averageWoundsTreatedPerMonth || "-"}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                                <Box sx={cardSx}>
                                    <SectionHeader icon={<PersonOutlined fontSize="small" />} title="Account Representative Info" />
                                    <Box sx={{ px: 2.8, py: 1 }}>
                                        <InfoRow
                                            label="Account Representative"
                                            value={accountRepresentativeData?.length > 0 ? (`${accountRepresentativeData[0]?.firstName + " "} ${accountRepresentativeData[0]?.middleName ? accountRepresentativeData[0]?.middleName + " " : ""} ${accountRepresentativeData[0]?.lastName}`) : "-"}
                                        />
                                        <InfoRow
                                            label="Agency"
                                            value={accountRepresentativeData?.length > 0 ? accountRepresentativeData[0]?.agencyName : "-"}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) && (
                                <>
                                    {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                                        <Box sx={cardSx}>
                                            <SectionHeader icon={<ListAltOutlined fontSize="small" />} title="Claim Info" />
                                            <Box sx={{ px: 2.8, py: 1 }}>
                                                <Controller
                                                    name="isClaimSupported"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <SwitchTextTrack
                                                                    {...field}
                                                                    name={field.name}
                                                                    checked={practiceEditInfo?.isClaimSupported || false}
                                                                    size="medium"
                                                                />
                                                            }
                                                            disabled={true}
                                                            label="Claim Support"
                                                            labelPlacement="start"
                                                        />
                                                    )}
                                                />
                                                {practiceEditInfo?.isClaimSupported === true && (
                                                    <InfoRow label="Add Claim Due Days" value={practiceEditInfo?.claimDueDays || "-"} />
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={cardSx}>
                                        <SectionHeader
                                            icon={<Checklist fontSize="small" />}
                                            title="Auto-Eligibility"
                                            subtitle="Check the button to activate Auto - Eligibility Creation Service."
                                        />
                                        <Box sx={{ px: 2.8, py: 1 }}>
                                            <Controller
                                                name="isAutoIVREnabled"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <SwitchTextTrack
                                                                {...field}
                                                                name={field.name}
                                                                checked={practiceEditInfo?.isAutoIVREnabled || false}
                                                                size="medium"
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    field.onChange(isChecked);
                                                                }}
                                                            />
                                                        }
                                                        disabled={true}
                                                        label="Auto-Eligibility-Service (pVerify)"
                                                        labelPlacement="start"
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={cardSx}>
                                        <SectionHeader
                                            icon={<LocalHospitalOutlined fontSize="small" />}
                                            title="EMR Integration"
                                            subtitle="Check the button to activate EMR Integration Service."
                                        />
                                        <Box sx={{ px: 2.8, py: 1 }}>
                                            <Controller
                                                name="isEMREnabled"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <SwitchTextTrack
                                                                {...field}
                                                                name={field.name}
                                                                checked={practiceEditInfo?.isEMREnabled || false}
                                                                size="medium"
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    field.onChange(isChecked);
                                                                }}
                                                            />
                                                        }
                                                        disabled={true}
                                                        label="EMR Integration Service"
                                                        labelPlacement="start"
                                                    />
                                                )}
                                            />
                                            {practiceEditInfo?.isEMREnabled === true && (
                                                <>
                                                    <InfoRow label="EMR Integration Provider" value={practiceEditInfo?.emrIntegrationProvider} />
                                                    <InfoRow label="EMR Provider Practice ID" value={practiceEditInfo?.emrProviderPracticeId} />
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* CS-1997 - Free Shipping (P&B only) practice-level indicator */}
                                    {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                                        <Box sx={cardSx}>
                                            <SectionHeader
                                                icon={<AssignmentReturnedOutlined fontSize="small" />}
                                                title="Free Shipping (P&B only)"
                                                subtitle="Check the button to allow free shipping for this practice."
                                            />
                                            <Box sx={{ px: 2.8, py: 1 }}>
                                                <Controller
                                                    name="allowFreeShipping"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <SwitchTextTrack
                                                                    {...field}
                                                                    name={field.name}
                                                                    checked={practiceEditInfo?.allowFreeShipping || false}
                                                                    size="medium"
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        field.onChange(isChecked);
                                                                    }}
                                                                />
                                                            }
                                                            disabled={true}
                                                            label="Enable Free Shipping"
                                                            labelPlacement="start"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                        <SectionTabNav tabs={tableTabs} activeTab={selectedTableTab} onTabChange={setActiveTableTab} />

                        {selectedTableTab === "templates" && hasTemplatesTab && (
                            <Box sx={cardSx}>
                                <SectionHeader icon={<DescriptionOutlined fontSize="small" />} title="Practice Templates" />
                                <Box sx={sectionBodySx}>
                                    <Box sx={{ overflowX: "auto" }}>
                                        <MuiCustomTable
                                            sx={modernTableSx}
                                            columns={practiceTemplateColumn}
                                            rows={practiceTemplateRows ? practiceTemplateRows : []}
                                            disablePagination={true}
                                            actionComponent={(row: PracticeTemplateRow[]) =>
                                                practiceTemplateActionComponent(row)
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {selectedTableTab === "override" && isAdminUser && (
                            <Box sx={cardSx}>
                                <SectionHeader icon={<TrackChanges fontSize="small" />} title="Override Entity" />
                                <Box sx={sectionBodySx}>
                                    <Box sx={{ overflowX: "auto" }}>
                                        <MuiCustomTable
                                            sx={modernTableSx}
                                            columns={overrideEntityColumns}
                                            rows={commissionRow ? commissionRow : []}
                                            disablePagination={true}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {selectedTableTab === "signers" && (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
                            <Box sx={cardSx}>
                                <SectionHeader icon={<GroupsOutlined fontSize="small" />} title="Authorized Signers" />
                                <Box sx={sectionBodySx}>
                                    <Box sx={{ overflowX: "auto" }}>
                                        <MuiCustomTable
                                            sx={modernTableSx}
                                            columns={authorizedSignerColumns}
                                            rows={authorizedSignersInfo ? authorizedSignersInfo : []}
                                            disablePagination={true}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={cardSx}>
                                <SectionHeader icon={<PlaceOutlined fontSize="small" />} title="Locations" />
                                <Box sx={sectionBodySx}>
                                    <Box sx={{ overflowX: "auto" }}>
                                        <MuiCustomTable
                                            sx={modernTableSx}
                                            columns={locationColumns}
                                            rows={modifiedPracticeLocationInfo ? modifiedPracticeLocationInfo : []}
                                            disablePagination={true}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        )}

                        {selectedTableTab === "authorizedUsers" && hasAuthorizedUsersTab && (
                            <Box sx={cardSx}>
                                <SectionHeader icon={<GroupsOutlined fontSize="small" />} title="Authorized User To Sign On Provider’s Behalf" />
                                <Box sx={sectionBodySx}>
                                    <Box sx={{ overflowX: "auto" }}>
                                        {groups.map((g: any) => (
                                            <Accordion
                                                key={g.providerId}
                                                disableGutters
                                                square
                                                sx={{
                                                    bgcolor: "transparent",
                                                    mt: 1,
                                                    mb: 2,
                                                    borderRadius: 1,
                                                    border: "1px solid",
                                                    borderColor: mTokens.border,
                                                }}
                                            >
                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            width: "100%",
                                                            flexWrap: "nowrap",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                display: "inline-flex",
                                                                alignItems: "baseline",
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Typography component="span">Provider:</Typography>

                                                            {g.hasProfile && canAccessUsersList && canAccessProvidersList ? (
                                                                <Link
                                                                    component="button"
                                                                    underline="hover"
                                                                    variant="h6"
                                                                    sx={{ fontWeight: 800, color: mTokens.accent }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleUserListNavigate(g.providerRaw);
                                                                    }}
                                                                >
                                                                    {g.providerName}
                                                                </Link>
                                                            ) : (
                                                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{g.providerName}</Typography>
                                                            )}
                                                        </Box>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ whiteSpace: "nowrap" }}
                                                        >
                                                            <span style={{ color: "#66BB6A", fontWeight: "bold", fontSize: "14px" }}>
                                                                {g.users.length}
                                                            </span>{" "}
                                                            Authorized User(S)
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ p: 0 }}>
                                                    <Box
                                                        sx={{
                                                            ...modernTableSx,
                                                            overflowX: "auto",
                                                            mx: 1.25,
                                                            mb: 1.25,
                                                            border: `1px solid ${mTokens.border}`,
                                                            borderRadius: "12px",
                                                        }}
                                                    >
                                                        <Table stickyHeader sx={{ minWidth: 720, background: "transparent !important" }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ width: { xs: "50%", md: "40%" } }}>
                                                                        Authorized User
                                                                    </TableCell>
                                                                    <TableCell sx={{ width: { xs: "22%", md: "15%" } }}>
                                                                        Email Notification
                                                                    </TableCell>
                                                                    <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: "15%" }}>
                                                                        Added/Modified By
                                                                    </TableCell>
                                                                    <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: "20%" }}>
                                                                        Added/Modified At
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {g.users.map((u: any, idx: number) => (
                                                                    <TableRow key={`${g.providerId}-${idx}`}>
                                                                        <TableCell>
                                                                            <Typography>{u.email} ({u.userRole})</Typography>
                                                                            <Box sx={{ display: { xs: "block", md: "none" }, mt: 0.5 }}>
                                                                                <Typography variant="caption" color="text.secondary">By: {u.addedBy}</Typography><br />
                                                                                <Typography variant="caption" color="text.secondary">At: {u.addedOn}</Typography>
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography sx={{ color: u.isNotificationEnabled ? "#1aa457" : "red" }}>
                                                                                {u.isNotificationEnabled ? "Yes" : "No"}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>{u.addedBy}</TableCell>
                                                                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>{u.addedOn}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {selectedTableTab === "providers" && (
                        <Box sx={cardSx}>
                            <SectionHeader icon={<LocalHospitalOutlined fontSize="small" />} title="Providers" />
                            <Box sx={sectionBodySx}>
                                <Box sx={{ overflowX: "auto" }}>
                                    <MuiCustomTable
                                        sx={modernTableSx}
                                        columns={providerColumn}
                                        rows={modifiedPracticeProvidersInfo ? modifiedPracticeProvidersInfo : []}
                                        disablePagination={true}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        )}
                    </Box>

                    <Box display="flex" justifyContent="end" mt="20px">
                        <Button
                            color="info"
                            variant="outlined"
                            removeUpperCase
                            startIcon={<ArrowBack />}
                            onClick={() => { navigate(-1) }}
                            sx={{
                                ...cancelBtnSx,
                                "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
                                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
                            }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            </form>
            <AuditLogDisplayPopup
                open={auditLogPopupShow.show}
                onClose={() => setAuditLogPopupShow({ auditLogData: [], show: false })}
                auditLogData={auditLogPopupShow.auditLogData}
            />
            <PDFViewer
                isOpen={showPDF}
                onClose={handleClose}
                fileName={document?.documentName + ""}
                title={document?.documentName + ""}
                base64String={document?.content || ""}
            />
            {showBaaPopup?.show &&
                <UploadBaaPopup
                    open={showBaaPopup.show}
                    onClose={() => setShowBaaPopup({ show: false, userEntity: "PRACTICE", details: null })}
                    userEntity={showBaaPopup.userEntity}
                    details={showBaaPopup.details}
                />
            }
        </>
    );
};

export default PracticeDetailsView;
